// src/utils/useLiveRace.ts
//
// Živá data závodu pro obrazovky živého bodování (docs/live-faze2-navrh.md
// bod 6): závod, stanoviště, rundy a volitelně souhrn výsledků – posluchači
// s metadaty (includeMetadataChanges), aby obrazovka poznala, jestli má stav
// ze serveru, nebo jen z místní mezipaměti (bez spojení).
//
// - Stanoviště jako u ostatních čtenářů: id = uložené `id`, jinak id
//   dokumentu (effectiveStationId), navíc `docId` pro zápisy (zapisuje se
//   vždy do dokumentu z načtení, ne podle id – posunutý závod OM Slovenska
//   open). Řazení podle `number`, pak podle id.
// - Rundy podle `number`, `docId` pro zápisy.
// - Souhrn výsledků pro zámky nastavení: id stanovišť zmíněná ve výsledcích
//   (stations, lockedStations, lastStationId), startovní čísla s výsledkem
//   (aspoň jedno stanoviště nebo rozstřel) a všechna čísla s dokumentem.
//
// Fáze 2 ho používá v nastavení, fáze 3 na obrazovce rozhodčího
// (setupOnlyWhenActive – stanoviště a rundy jen za běhu závodu); fáze 4a
// tabule (resultDocs, keepDataOnError) a Živé výsledky (závod a rundy –
// stations: false, rounds až s ověřeným přístupem).
//
// Fáze 4a (docs/live-faze4-navrh.md, A1 a A2):
// - resultDocs: true = i dokumenty live_results podle startovního čísla
//   (jeden posluchač se souhrnem); mapa se mění jen se změnou dokumentů,
//   ne s pouhou změnou metadat;
// - keepDataOnError: true = chyba posluchače PO prvním snímku nechá poslední
//   data (stav zůstane "ready", stale = true) a posluchač se za
//   LISTEN_RETRY_MS přihlásí znovu (SDK posluchač po chybě ukončí) – tabule
//   na TV nesmí zamrznout ani spadnout na chybovou stránku; chyba před
//   prvním snímkem jako dřív "error".

import { useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import type { LiveResult, LiveStation, RoundShooter } from "../types/liveScoring";
import { effectiveStationId, stationIdsInResult } from "./liveStationIds";
import { reportWarning } from "./reportError";

/** Stanoviště s id dokumentu (cíl zápisů). */
export interface LiveStationEntry extends LiveStation {
  docId: string;
}

/** Runda s id dokumentu (cíl zápisů). */
export interface LiveRoundEntry {
  docId: string;
  number: number;
  shooters: RoundShooter[];
}

/** Souhrn živých výsledků pro zámky nastavení (bod 2.2 návrhu fáze 2). */
export interface LiveResultsSummary {
  /** Id stanovišť zmíněná ve výsledcích (klíče stations, lockedStations, lastStationId). */
  stationIds: Set<string>;
  /** Id stanoviště → počet střelců s výsledkem na něm (klíč v stations). */
  stationCounts: Map<string, number>;
  /** Id stanoviště → startovní čísla s výsledkem na něm (seřazená) – dopad změny nastavení stanoviště. */
  stationNumbers: Map<string, number[]>;
  /** Startovní čísla s výsledkem: aspoň jedno stanoviště nebo rozstřel. */
  scored: Map<number, { stations: number; tiebreaker: boolean }>;
  /** Startovní čísla, pod kterými existuje dokument výsledku (i prázdný). */
  docNumbers: Set<number>;
}

export type LiveRaceStatus = "loading" | "ready" | "notFound" | "error";

/** Za jak dlouho se posluchač po chybě přihlásí znovu (keepDataOnError). */
export const LISTEN_RETRY_MS = 60 * 1000;

export interface UseLiveRaceResult {
  status: LiveRaceStatus;
  /** Závod (id + data), null před načtením nebo když neexistuje. */
  race: ({ id: string } & Record<string, any>) | null;
  stations: LiveStationEntry[];
  rounds: LiveRoundEntry[];
  /** Souhrn výsledků (jen s options.results), null do prvního snímku. */
  results: LiveResultsSummary | null;
  /** Všichni posluchači mají poslední snímek ze serveru (ne z mezipaměti). */
  serverSynced: boolean;
  /** Některý snímek obsahuje místní zápis, který server ještě nepotvrdil. */
  hasPendingWrites: boolean;
  /** Chyba posluchače závodu, stanovišť nebo rund (status 'error'). */
  error: unknown;
  /** Chyba posluchače výsledků – zámky nejsou známé (obrazovka zamkne mazání). */
  resultsError: unknown;
  /** Dokumenty live_results podle startovního čísla (jen s options.resultDocs), null do prvního snímku. */
  resultDocs: { [startNumber: number]: LiveResult } | null;
  /** Některý posluchač po prvním snímku selhal a data jsou z doby před chybou (keepDataOnError). */
  stale: boolean;
}

type Part = "race" | "stations" | "rounds" | "results";

interface PartState {
  loaded: boolean;
  fromCache: boolean;
  pending: boolean;
  error: unknown;
  /** Posluchač po prvním snímku selhal, data zůstala (keepDataOnError). */
  failed: boolean;
}

const initialPart = (): PartState => ({ loaded: false, fromCache: true, pending: false, error: null, failed: false });
/** Výchozí stav výsledků (stejný objekt – vypnutý posluchač nevykresluje znovu). */
const initialResultsPart: PartState = initialPart();

/** Posluchači s metadaty – obrazovka pozná snímek z mezipaměti. */
const LISTEN_OPTIONS = { includeMetadataChanges: true };

type SetParts = (update: (prev: Record<Part, PartState>) => Record<Part, PartState>) => void;

/** Změna stavu jedné části; beze změny zůstane stejný objekt (žádné vykreslení navíc). */
function patchPart(setParts: SetParts, part: Part, patch: Partial<PartState>) {
  setParts((prev) => {
    const cur = prev[part];
    const next = { ...cur, ...patch };
    if (
      next.loaded === cur.loaded &&
      next.fromCache === cur.fromCache &&
      next.pending === cur.pending &&
      next.error === cur.error &&
      next.failed === cur.failed
    ) {
      return prev;
    }
    return { ...prev, [part]: next };
  });
}

/** Startovní číslo dokumentu výsledku: pole startNumber, jinak id dokumentu (jako AdminShootOffs). */
function resultStartNumber(docId: string, data: Record<string, unknown>): number | null {
  if (typeof data.startNumber === "number" && Number.isFinite(data.startNumber)) return data.startNumber;
  const n = Number(docId);
  return Number.isFinite(n) && docId.trim() !== "" ? n : null;
}

/** Souhrn výsledků z dokumentů live_results. */
export function summarizeLiveResults(docs: Array<{ id: string; data: Record<string, unknown> }>): LiveResultsSummary {
  const summary: LiveResultsSummary = {
    stationIds: new Set<string>(),
    stationCounts: new Map<string, number>(),
    stationNumbers: new Map<string, number[]>(),
    scored: new Map<number, { stations: number; tiebreaker: boolean }>(),
    docNumbers: new Set<number>(),
  };
  docs.forEach(({ id, data }) => {
    stationIdsInResult(data).forEach((s) => summary.stationIds.add(s));
    const stations = data.stations && typeof data.stations === "object" ? Object.keys(data.stations as object) : [];
    stations.forEach((s) => summary.stationCounts.set(s, (summary.stationCounts.get(s) || 0) + 1));
    const sn = resultStartNumber(id, data);
    if (sn === null) return;
    stations.forEach((s) => summary.stationNumbers.set(s, [...(summary.stationNumbers.get(s) || []), sn]));
    summary.docNumbers.add(sn);
    const tiebreaker = typeof data.tiebreaker === "number" && data.tiebreaker > 0;
    if (stations.length > 0 || tiebreaker) summary.scored.set(sn, { stations: stations.length, tiebreaker });
  });
  summary.stationNumbers.forEach((list, key) => summary.stationNumbers.set(key, list.sort((a, b) => a - b)));
  return summary;
}

function sortStations(list: LiveStationEntry[]): LiveStationEntry[] {
  return list.sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Startovní číslo → dokument výsledku (klíč jako summarizeLiveResults). */
function resultDocsOf(docs: Array<{ id: string; data: Record<string, unknown> }>): { [startNumber: number]: LiveResult } {
  const map: { [startNumber: number]: LiveResult } = {};
  docs.forEach(({ id, data }) => {
    const sn = resultStartNumber(id, data);
    if (sn !== null) map[sn] = data as unknown as LiveResult;
  });
  return map;
}

export interface UseLiveRaceOptions {
  /** false = nic nečte (např. do potvrzení přihlášení). */
  enabled?: boolean;
  /** Souhrn live_results (zámky nastavení). */
  results?: boolean;
  /** Dokumenty live_results podle startovního čísla (tabule). */
  resultDocs?: boolean;
  /** Stanoviště a rundy jen za běhu závodu (obrazovka rozhodčího). */
  setupOnlyWhenActive?: boolean;
  /** Posluchač stanovišť (výchozí ano; Živé výsledky mají vlastní s čekáním na server). */
  stations?: boolean;
  /** Posluchač rund (výchozí ano; Živé výsledky až s ověřeným přístupem). */
  rounds?: boolean;
  /** Po chybě posluchače nechat poslední data a přihlásit se znovu (tabule). */
  keepDataOnError?: boolean;
}

/**
 * Posluchači závodu. `enabled: false` = nic nečte (např. do potvrzení
 * přihlášení); `results: true` = i souhrn live_results – vlastní posluchač,
 * jde zapnout i později (po ověření přístupu) bez nového načtení závodu;
 * `setupOnlyWhenActive: true` = stanoviště a rundy jen, když závod běží
 * (obrazovka rozhodčího – ukončený a nespuštěný závod je nepotřebují);
 * jinak jsou prázdné a o stavu rozhoduje jen závod. Ostatní volby viz
 * UseLiveRaceOptions a hlavička souboru.
 */
export function useLiveRace(
  raceId: string | null | undefined,
  options: UseLiveRaceOptions = {}
): UseLiveRaceResult {
  const enabled = options.enabled !== false && !!raceId;
  const withSummary = !!options.results;
  const withDocs = !!options.resultDocs;
  const withResults = withSummary || withDocs;
  const onlyWhenActive = !!options.setupOnlyWhenActive;
  const wantStations = options.stations !== false;
  const wantRounds = options.rounds !== false;
  const keepData = !!options.keepDataOnError;

  const [race, setRace] = useState<({ id: string } & Record<string, any>) | null>(null);
  const [raceMissing, setRaceMissing] = useState(false);
  const [stations, setStations] = useState<LiveStationEntry[]>([]);
  const [rounds, setRounds] = useState<LiveRoundEntry[]>([]);
  const [results, setResults] = useState<LiveResultsSummary | null>(null);
  const [docs, setDocs] = useState<{ [startNumber: number]: LiveResult } | null>(null);
  const [parts, setParts] = useState<Record<Part, PartState>>({
    race: initialPart(),
    stations: initialPart(),
    rounds: initialPart(),
    results: initialResultsPart,
  });
  // Nové přihlášení posluchače po chybě (keepDataOnError) – data se nenulují
  const [retry, setRetry] = useState<Record<Part, number>>({ race: 0, stations: 0, rounds: 0, results: 0 });

  // Stanoviště a rundy: vždy, nebo jen za běhu závodu (setupOnlyWhenActive)
  const withSetup = !onlyWhenActive || (!!race && race.status === "active");
  const withStations = withSetup && wantStations;
  const withRounds = withSetup && wantRounds;

  /**
   * Chyba posluchače: bez keepDataOnError (nebo před prvním snímkem) chyba
   * části; jinak data zůstanou, část je „failed" a za LISTEN_RETRY_MS se
   * posluchač přihlásí znovu.
   */
  const failHandler = (part: Part, isActive: () => boolean, timers: Array<ReturnType<typeof setTimeout>>) =>
    (err: unknown) => {
      reportWarning(`useLiveRace.${part}`, err, { raceId });
      if (!isActive()) return;
      setParts((prev) => {
        const cur = prev[part];
        if (keepData && cur.loaded) {
          return { ...prev, [part]: { ...cur, failed: true, fromCache: true } };
        }
        return { ...prev, [part]: { ...cur, error: err } };
      });
      if (keepData) {
        timers.push(
          setTimeout(() => {
            if (isActive()) setRetry((prev) => ({ ...prev, [part]: prev[part] + 1 }));
          }, LISTEN_RETRY_MS)
        );
      }
    };

  // Závod (nové přihlášení po chybě data nenuluje)
  const raceKey = `${raceId}|${enabled}`;
  const lastRaceKey = useRef<string | null>(null);
  useEffect(() => {
    if (lastRaceKey.current !== raceKey) {
      lastRaceKey.current = raceKey;
      setRace(null);
      setRaceMissing(false);
      setParts((prev) => ({ ...prev, race: initialPart() }));
    }
    if (!enabled || !raceId) return undefined;

    let active = true;
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    const unsub = onSnapshot(
      doc(db, "races", raceId),
      LISTEN_OPTIONS,
      (snap) => {
        if (!active) return;
        if (!snap.exists()) {
          // Z mezipaměti „neexistuje“ ještě nic neznamená – počká na server
          if (!snap.metadata.fromCache) {
            setRace(null);
            setRaceMissing(true);
          }
        } else {
          // Id dokumentu má přednost před uloženým polem id (starší úprava závodu)
          setRace({ ...(snap.data() as Record<string, any>), id: snap.id });
          setRaceMissing(false);
        }
        patchPart(setParts, "race", {
          loaded: snap.exists() || !snap.metadata.fromCache,
          fromCache: snap.metadata.fromCache,
          pending: snap.metadata.hasPendingWrites,
          error: null,
          failed: false,
        });
      },
      failHandler("race", () => active, timers)
    );
    return () => {
      active = false;
      timers.forEach((t) => clearTimeout(t));
      unsub();
    };
    // failHandler je nová funkce při každém vykreslení; čte raceId (je
    // v závislostech) a keepData (pevné nastavení volajícího) – v závislostech
    // by posluchače přihlašovala znovu při každém vykreslení
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId, enabled, raceKey, retry.race]);

  // Stanoviště
  const stationsKey = `${raceId}|${enabled}|${withStations}`;
  const lastStationsKey = useRef<string | null>(null);
  useEffect(() => {
    if (lastStationsKey.current !== stationsKey) {
      lastStationsKey.current = stationsKey;
      setStations([]);
      setParts((prev) => ({ ...prev, stations: initialPart() }));
    }
    if (!enabled || !raceId || !withStations) return undefined;

    let active = true;
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    const unsub = onSnapshot(
      collection(db, `races/${raceId}/live_stations`),
      LISTEN_OPTIONS,
      (snap) => {
        if (!active) return;
        const list = snap.docs.map((d) => {
          const data = (d.data() || {}) as Record<string, any>;
          return {
            ...data,
            id: effectiveStationId(d.id, data),
            docId: d.id,
            judgeUids: Array.isArray(data.judgeUids) ? data.judgeUids : [],
          } as LiveStationEntry;
        });
        setStations(sortStations(list));
        patchPart(setParts, "stations", {
          loaded: true,
          fromCache: snap.metadata.fromCache,
          pending: snap.metadata.hasPendingWrites,
          error: null,
          failed: false,
        });
      },
      failHandler("stations", () => active, timers)
    );
    return () => {
      active = false;
      timers.forEach((t) => clearTimeout(t));
      unsub();
    };
    // failHandler je nová funkce při každém vykreslení; čte raceId (je
    // v závislostech) a keepData (pevné nastavení volajícího) – v závislostech
    // by posluchače přihlašovala znovu při každém vykreslení
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId, enabled, withStations, stationsKey, retry.stations]);

  // Rundy
  const roundsKey = `${raceId}|${enabled}|${withRounds}`;
  const lastRoundsKey = useRef<string | null>(null);
  useEffect(() => {
    if (lastRoundsKey.current !== roundsKey) {
      lastRoundsKey.current = roundsKey;
      setRounds([]);
      setParts((prev) => ({ ...prev, rounds: initialPart() }));
    }
    if (!enabled || !raceId || !withRounds) return undefined;

    let active = true;
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    const unsub = onSnapshot(
      collection(db, `races/${raceId}/live_rounds`),
      LISTEN_OPTIONS,
      (snap) => {
        if (!active) return;
        const list = snap.docs
          .map((d) => {
            const data = (d.data() || {}) as Record<string, any>;
            return {
              docId: d.id,
              number: typeof data.number === "number" ? data.number : Number(data.number) || 0,
              shooters: Array.isArray(data.shooters) ? (data.shooters as RoundShooter[]) : [],
            };
          })
          .sort((a, b) => a.number - b.number || (a.docId < b.docId ? -1 : 1));
        setRounds(list);
        patchPart(setParts, "rounds", {
          loaded: true,
          fromCache: snap.metadata.fromCache,
          pending: snap.metadata.hasPendingWrites,
          error: null,
          failed: false,
        });
      },
      failHandler("rounds", () => active, timers)
    );
    return () => {
      active = false;
      timers.forEach((t) => clearTimeout(t));
      unsub();
    };
    // failHandler je nová funkce při každém vykreslení; čte raceId (je
    // v závislostech) a keepData (pevné nastavení volajícího) – v závislostech
    // by posluchače přihlašovala znovu při každém vykreslení
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId, enabled, withRounds, roundsKey, retry.rounds]);

  // Výsledky (souhrn a/nebo dokumenty) – vlastní posluchač: volající ho smí
  // zapnout až později (nastavení až s ověřeným přístupem) bez nového
  // načtení závodu
  const resultsKey = `${raceId}|${enabled}|${withSummary}|${withDocs}`;
  const lastResultsKey = useRef<string | null>(null);
  useEffect(() => {
    if (lastResultsKey.current !== resultsKey) {
      lastResultsKey.current = resultsKey;
      setResults(null);
      setDocs(null);
      setParts((prev) => (prev.results === initialResultsPart ? prev : { ...prev, results: initialResultsPart }));
    }
    if (!enabled || !raceId || !withResults) return undefined;

    let active = true;
    let first = true;
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    const unsub = onSnapshot(
      collection(db, `races/${raceId}/live_results`),
      LISTEN_OPTIONS,
      (snap) => {
        if (!active) return;
        // Snímek jen se změnou metadat (potvrzení serverem) data nemění
        const changed = first || snap.docChanges().length > 0;
        first = false;
        if (changed) {
          const list = snap.docs.map((d) => ({ id: d.id, data: (d.data() || {}) as Record<string, unknown> }));
          if (withSummary) setResults(summarizeLiveResults(list));
          if (withDocs) setDocs(resultDocsOf(list));
        }
        patchPart(setParts, "results", {
          loaded: true,
          fromCache: snap.metadata.fromCache,
          pending: snap.metadata.hasPendingWrites,
          error: null,
          failed: false,
        });
      },
      failHandler("results", () => active, timers)
    );
    return () => {
      active = false;
      timers.forEach((t) => clearTimeout(t));
      unsub();
    };
    // failHandler je nová funkce při každém vykreslení; čte raceId (je
    // v závislostech) a keepData (pevné nastavení volajícího) – v závislostech
    // by posluchače přihlašovala znovu při každém vykreslení
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId, enabled, withResults, withSummary, withDocs, resultsKey, retry.results]);

  return useMemo<UseLiveRaceResult>(() => {
    // Jádro (závod, stanoviště, rundy) rozhoduje o stavu stránky; výsledky
    // jen o zámcích – jejich chyba stránku neshodí
    const core: Part[] = ["race"];
    if (withStations) core.push("stations");
    if (withRounds) core.push("rounds");
    const used: Part[] = withResults ? [...core, "results"] : core;
    const error = core.map((p) => parts[p].error).find((e) => e !== null && e !== undefined) || null;
    const resultsError = withResults ? parts.results.error || null : null;
    let status: LiveRaceStatus = "loading";
    if (!enabled) status = "loading";
    else if (raceMissing) status = "notFound";
    else if (error) status = "error";
    else if (core.every((p) => parts[p].loaded) && race) status = "ready";
    const stale = used.some((p) => parts[p].failed);
    return {
      status,
      race,
      stations,
      rounds,
      results: withSummary && !resultsError ? results : null,
      resultDocs: withDocs && !resultsError ? docs : null,
      // Výsledky s chybou spojení nerozhodují (zámky řeší resultsError)
      serverSynced:
        enabled &&
        !error &&
        !stale &&
        used.every((p) => (p === "results" && resultsError) || (parts[p].loaded && !parts[p].fromCache)),
      hasPendingWrites: used.some((p) => parts[p].pending),
      error,
      resultsError,
      stale,
    };
  }, [enabled, withResults, withSummary, withDocs, withStations, withRounds, parts, raceMissing, race, stations, rounds, results, docs]);
}
